import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core"
import { getPronouns } from "actions/authentication"
import AppContext from "contexts/App"
import { Form, Formik } from "formik"
import { useContext, useRef } from "react"
import InputMask from "react-input-mask"
import { useQuery, useQueryClient } from "react-query"
import formatPhoneNumber from "utils/formatPhone"
import * as Yup from "yup"

import LoaderButton from "../../../components/LocalLoadingButton"
import useStyles from "./styles"

const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/

const AccountDetailsForm = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()

  const {
    dialogs,
    token,
    authUser,
    setAuthData,
    updateUserProfileMutation,
    openSnackBar,
    isClerk,
  } = useContext(AppContext)
  const thisDialog = dialogs?.["clerkMyAccount"] || {}
  const { open = false, data = {} } = thisDialog

  const formRef = useRef()

  const schema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Please enter a valid email")
      .required(`Required`),
    pronoun_id: Yup.number().nullable(true),
    phone: Yup.string()
      .required("Required")
      .matches(phoneRegex, "Phone number is not valid")
      .min(14, "Phone number is not valid")
      .max(14, "Phone number is not valid"),
    //14 char - 9 numbers, 3 special chars ()-, 1 space
  })

  const { data: pronounsList, isLoading: pronounsListLoading } = useQuery(
    "pronouns",
    () => getPronouns(isClerk)
  )

  // const handleChangeCity = (event) => {
  //   setCity(event.target.value)
  // }

  // const handleChangeRole = (event) => {
  //   setRole(event.target.value)
  // }

  const roleInitialValues = () => {
    if (authUser?.super_admin === 1 && authUser?.approved === 1) {
      return "clerkAdmin"
    } else if (authUser?.super_admin === 1 && authUser?.approved === 0) {
      return "admin"
    } else {
      return "clerk"
    }
  }

  const onSubmit = (values, actions) => {
    const data = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      phone: values.phone.replace(/[^0-9]/g, ""),
      pronoun_id: values.pronoun_id,
    }
    return new Promise((resolve, reject) => {
      if (formRef.current.dirty) {
        updateUserProfileMutation.mutate(data, {
          onSuccess: (res) => {
            let userData = {
              ...res.data,
              phone: values.phone,
            }
            setAuthData({
              token: token,
              account: userData,
            })
            queryClient.setQueryData("accountDetails", (oldData) => {
              return userData
            })
            openSnackBar({
              message: "Account Details successfully updated!",
            })
            resolve()
          },
          onError: (err) => {
            openSnackBar({
              message: "Failed to update Account Details, please retry",
            })
            reject()
          },
        })
      } else {
        // no change to info, no call made - move on.
        resolve()
      }
    })
  }

  return (
    <Formik
      initialValues={{
        firstName: authUser?.first_name || "",
        lastName: authUser?.last_name || "",
        email: authUser?.email || "",
        phone: formatPhoneNumber(authUser?.phone) || "",
        pronoun_id: authUser?.pronoun_id || null,
      }}
      onSubmit={onSubmit}
      validationSchema={schema}
      innerRef={formRef}
      enableReinitialize={true}
    >
      {({
        dirty,
        handleChange,
        values,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        handleSubmit,
        setFieldValue,
      }) =>
        pronounsListLoading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress size={15} />
          </Box>
        ) : (
          <Form onSubmit={handleSubmit} className={classes.formContainer}>
            <Typography variant="h5" className={classes.contentTitles}>
              Account Details
            </Typography>
            <Box display="flex" flexWrap="wrap">
              <TextField
                name="firstName"
                label="First Name"
                variant="outlined"
                style={{ maxWidth: "175px", marginBottom: "25px" }}
                value={values?.firstName}
                className={classes.textfield}
                helperText={touched.firstName && errors.firstName}
                error={Boolean(touched.firstName && errors.firstName)}
                onChange={handleChange}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <TextField
                name="lastName"
                label="Last Name"
                variant="outlined"
                style={{ maxWidth: "175px", marginBottom: "25px" }}
                value={values?.lastName}
                className={classes.textfield}
                helperText={touched.lastName && errors.lastName}
                error={Boolean(touched.lastName && errors.lastName)}
                onChange={handleChange}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <TextField
                name="email"
                label="Work Email"
                variant="outlined"
                style={{ maxWidth: "180px", marginBottom: "25px" }}
                value={values?.email}
                className={classes.textfield}
                helperText={touched.email && errors.email}
                error={Boolean(touched.email && errors.email)}
                onChange={handleChange}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <InputMask
                id="phone-mask"
                mask="(999) 999-9999"
                maskChar="_"
                value={values?.phone}
                onChange={handleChange}
              >
                {() => (
                  <TextField
                    name="phone"
                    label="Work Phone"
                    variant="outlined"
                    style={{ maxWidth: "180px", marginBottom: "25px" }}
                    className={classes.textfield}
                    helperText={touched.phone && errors.phone}
                    error={Boolean(touched.phone && errors.phone)}
                    FormHelperTextProps={{
                      className: classes.formHelperText,
                    }}
                  />
                )}
              </InputMask>
              <TextField
                label="Pronouns"
                select
                name="pronoun_id"
                value={values?.pronoun_id}
                onChange={(e) => setFieldValue("pronoun_id", e.target.value)}
                variant="outlined"
                style={{
                  backgroundColor: "#fff",
                  width: "180px",
                  marginBottom: "25px",
                }}
                className={classes.textField}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              >
                {pronounsList?.data?.pronouns?.map((p) => {
                  return (
                    <MenuItem key={p?.id} value={p?.id}>
                      {p?.pronoun}
                    </MenuItem>
                  )
                })}
              </TextField>
            </Box>
            <Box display="flex" justifyContent="center">
              <LoaderButton
                color="primary"
                variant="contained"
                // onClick={onSubmit}
                type="submit"
                className={classes.saveButton}
                classoverrides={{
                  wrapper: classes.button,
                  button: {
                    root: classes.buttonRoot,
                  },
                }}
                disabled={!dirty}
                working={isSubmitting}
              >
                Save Changes
              </LoaderButton>
            </Box>
          </Form>
        )
      }
    </Formik>
  )
}

export default AccountDetailsForm
